import { Loader2 } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PRIORITY_OPTIONS } from "./shared";

interface OwnerOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface PositionCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export function PositionCreateDialog({ open, onOpenChange, onCreated }: PositionCreateDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [owners, setOwners] = useState<OwnerOption[]>([]);
  const [hiringManagerId, setHiringManagerId] = useState<string>("none");
  const [priority, setPriority] = useState<string>("medium");

  useEffect(() => {
    if (!open) return;
    fetch("/api/owners?pageSize=1000")
      .then((res) => (res.ok ? res.json() : { owners: [] }))
      .then((data) => setOwners(data.owners ?? []))
      .catch(() => setOwners([]));
  }, [open]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const body = {
      title: formData.get("title") as string,
      team: (formData.get("team") as string) || null,
      hiringManagerId: hiringManagerId === "none" ? null : hiringManagerId,
      priority,
      dateOpened: (formData.get("dateOpened") as string) || null,
      targetStartDate: (formData.get("targetStartDate") as string) || null,
      notes: (formData.get("notes") as string) || null,
    };

    try {
      const res = await fetch("/api/recruitment/positions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Failed to create position");
        return;
      }

      onOpenChange(false);
      onCreated();
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setError("");
      setHiringManagerId("none");
      setPriority("medium");
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Open Position</DialogTitle>
          <DialogDescription>Create a new role to start tracking candidates for.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="position-title">Title</Label>
            <Input
              id="position-title"
              name="title"
              required
              placeholder="e.g. Relational Consultant"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="position-team">Team (optional)</Label>
              <Input id="position-team" name="team" placeholder="e.g. FP Consultants" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="position-priority">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger id="position-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="position-hiring-manager">Hiring Manager (optional)</Label>
            <Select value={hiringManagerId} onValueChange={setHiringManagerId}>
              <SelectTrigger id="position-hiring-manager">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {owners.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.firstName} {o.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="position-date-opened">Date Opened (optional)</Label>
              <Input id="position-date-opened" name="dateOpened" type="date" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="position-target-start">Target Start Date (optional)</Label>
              <Input id="position-target-start" name="targetStartDate" type="date" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="position-notes">Notes (optional)</Label>
            <Textarea id="position-notes" name="notes" rows={2} />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Position
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
